// ABOUTME: Self-contained component for displaying shipping/label creation progress.
// ABOUTME: Handles its own polling via RTK Query and controls when it can be dismissed.

import { Box, IconButton, useTheme } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { tokens } from "../../../../../theme";
import { ShCopyButtonWithOwnChildren } from "../../../../theme/ShCopy";
import Text from "../../../../theme/text/Text";
import Progressbar from "../../../../theme/Progressbar";
import SHModal from "../../../../theme/Modal/SHModal";
import Shbutton from "../../../../theme/buttons/Shbutton";
import SidepanelLoader from "../../../../theme/sidepanel2/SidepanelLoader";
import { useGetShippingProgressQuery } from "../../../../../newapi/pickingjob/pickingjobSlice";
import Subheading from "../../../../theme/text/Subheading";

const SpOrderDetails = SidepanelLoader(() => import("../../../../global/Sidepanels/SpOrderDetails/SpOrderDetails"));

const MAX_CONSECUTIVE_ERRORS = 10;

const ShippingProgressAction = ({ action, onRemove }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();
    const [errorsModalOpen, setErrorsModalOpen] = useState(false);
    const [slowPoll, setSlowPoll] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);
    const consecutiveErrors = useRef(0);

    const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [spLoading, setSpLoading] = useState(false);

    const handleOpenOrderDetails = (orderId) => {
        setSelectedOrderId(orderId);
        setSpLoading(true);
        setOrderDetailsOpen(true);
    };

    const { data, isError } = useGetShippingProgressQuery(action.entityId, {
        pollingInterval: hasFailed ? 0 : (slowPoll ? 5000 : 2000),
    });

    const progress = data?.progress;
    const done = progress?.done ?? 0;
    const total = progress?.total ?? 0;
    const errorCount = progress?.error ?? 0;
    const errors = progress?.errors ?? [];

    useEffect(() => {
        if (isError || (data && progress === null)) {
            consecutiveErrors.current += 1;
            if (consecutiveErrors.current >= MAX_CONSECUTIVE_ERRORS) {
                setHasFailed(true);
            }
            if (!slowPoll) {
                setSlowPoll(true);
            }
        } else if (data && progress) {
            consecutiveErrors.current = 0;
            if (slowPoll) {
                setSlowPoll(false);
            }
        }
    }, [isError, data, progress, slowPoll]);

    const processed = done + errorCount;
    const isComplete = (total > 0 && processed >= total);
    const isDismissable = isComplete || hasFailed;

    const progressPercent = total > 0 ? `${Math.round((processed / total) * 100)}%` : "0%";

    return (
        <Box
            sx={{
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                borderRadius: 2,
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: hasFailed
                            ? colors.red[100]
                            : isComplete
                                ? colors.green[100]
                                : theme.palette.mode === "dark"
                                    ? colors.blue[100]
                                    : colors.primary[100],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: hasFailed
                            ? colors.red[400]
                            : isComplete
                                ? colors.green[400]
                                : theme.palette.mode === "dark"
                                    ? colors.blue[400]
                                    : colors.primary[400],
                    }}
                >
                    {hasFailed ? (
                        <ReportProblemOutlinedIcon fontSize="small" />
                    ) : isComplete ? (
                        <CheckCircleOutlineIcon fontSize="small" />
                    ) : (
                        <LocalShippingIcon fontSize="small" />
                    )}
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Text bold fontSize={13}>
                        {hasFailed ? t("action_failed") : isComplete ? t("labels_created") : t("creating_labels")}
                    </Text>
                </Box>
                <IconButton
                    size="small"
                    onClick={onRemove}
                    // disabled={!isDismissable}
                    sx={{
                        // opacity: isDismissable ? 1 : 0.3,
                        color: colors.txt["secondary"],
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Progress bar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <Progressbar
                        progress={progressPercent}
                        size={8}
                        status={hasFailed ? { variant: "red", value: null } : isComplete ? { variant: "green", value: null } : null}
                    />
                </Box>
                <Text fontSize={12} light>
                    {processed}/{total}
                </Text>
            </Box>

            {/* Errors button */}
            {errorCount > 0 && (
                <Shbutton
                    className={"space-end-icon"}
                    onClick={() => setErrorsModalOpen(true)}
                    color="red"
                    variant="contained"
                    endIcon={<ErrorOutlineIcon/>}
                >
                    {errorCount} {t("errors")}
                </Shbutton>
             )} 

            {/* Errors Modal */}
            <SHModal
                open={errorsModalOpen}
                onClose={() => setErrorsModalOpen(false)}
            >

                <Box
                    sx={{
                        p: 3,
                    }}
                >
                    <Subheading textAlign="center">{errorCount} {t("errors")}</Subheading>
                    
                </Box>
                
                <Box
                    sx={{
                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        padding:3,
                        borderRadius:4,
                        display:"flex",
                        flexDirection:"column",
                        gap:2,
                        overflowY:"auto",
                        maxHeight:"400px"
                    }}
                >
                    {errors.map((err) => (
                        <Box
                            key={err.id}
                            sx={{
                                background: colors.red[100],
                                borderRadius: 2,
                                display: "flex",
                                alignItems:'center',
                                gap: 1,
                                px:2,
                                // height:32,
                                maxWidth:700,
                                py:1,
                            }}
                        >
                            <Text bold color={colors.red[400]} sx={{minWidth:100,}}>
                                {err.barcode}
                            </Text>

                            <Text color={colors.red[400]}>
                                {err.error}
                            </Text>

                            <ShCopyButtonWithOwnChildren
                                value={`${err.barcode}: ${err.error}`}
                                sx={{
                                    height:24,
                                    width:24,
                                    flexShrink:0,
                                    ml:'auto',
                                    color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                    borderRadius:2,
                                    background:colors.red[400],
                                    alignItems:'center',
                                    justifyContent:'center',
                                    cursor:'pointer',
                                }}
                            >
                                <ContentCopyOutlinedIcon fontSize="small" />
                            </ShCopyButtonWithOwnChildren>

                        </Box>
                    ))}
                    </Box>
            </SHModal>

            {/* Order Details Sidepanel */}
            {spLoading && (
                <SpOrderDetails
                    isExtended={orderDetailsOpen}
                    setIsExtended={setOrderDetailsOpen}
                    orderId={selectedOrderId}
                />
            )}
        </Box>
    );
};

export default ShippingProgressAction;
